import { Box, FormControl, FormControlLabel, InputAdornment, Radio, RadioGroup, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { addDays, addMinutes, differenceInMinutes, getDay, getMonth } from 'date-fns';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { Shift } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { useSnackbar } from '../../contexts/snackbar.context';
import { db } from '../../firebase';
import { setDoc } from '../../utils/api';
import { getWeekDayLabels } from '../../utils/dates';

import EditModal from '../EditModal';
import FormSwitch from '../forms/FormSwitch';
import FormTextField from '../forms/FormTextField';

const setNewDateToTimeRange = (date: Date, start: number, end: number): [number, number] => {
  const diff = differenceInMinutes(end, start);
  const startDate = new Date(start);
  startDate.setDate(date.getDate());
  startDate.setMonth(date.getMonth());
  startDate.setFullYear(date.getFullYear());
  const endDate = addMinutes(new Date(startDate), diff);

  return [startDate.getTime(), endDate.getTime()];
};

type Props = {
  shift: DbDocument<Shift>;
  open: boolean;
  onClose: () => void;
};

type FormType = {
  mode: 'date' | 'perDay' | 'perWeek';
  day1: boolean;
  day2: boolean;
  day3: boolean;
  day4: boolean;
  day5: boolean;
  day6: boolean;
  day7: boolean;
  perDays: number | string;
};

const DuplicateModal = (props: Props) => {
  const { shift, open, onClose } = props;

  const initDateTime = useMemo(() => addDays(shift.start, 1), [shift.start]);

  const [date, setDate] = useState<Date | null>(initDateTime);

  const { lang } = useLocale();
  const { cafeSlug } = useAdmin();
  const { addSnackbar } = useSnackbar();

  const dayOfTheWeek = useMemo(() => getWeekDayLabels(lang), [lang]);

  const defaultValues: FormType = useMemo(() => {
    const day = getDay(shift.start);
    return {
      mode: 'date',
      day1: day === 1,
      day2: day === 2,
      day3: day === 3,
      day4: day === 4,
      day5: day === 5,
      day6: day === 6,
      day7: day === 0,
      perDays: 2,
    };
  }, [shift.start]);

  const form = useForm<FormType>({ mode: 'onChange', defaultValues });

  const { watch, setValue } = form;
  const mode = watch('mode');

  const handleChangeMode = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue('mode', event.target.value as FormType['mode']);
    },
    [setValue]
  );

  const saveShift = useCallback(
    async (date: Date) => {
      const [start, end] = setNewDateToTimeRange(date, shift.start, shift.end);

      const q = query(
        collection(db, 'cafes', cafeSlug, 'shifts'),
        where('workerId', '==', shift.workerId),
        where('start', '>=', start),
        where('start', '<=', end)
      );

      const querySnapshot = await getDocs(q);
      let isDublicate = false;

      querySnapshot.forEach((doc) => {
        const { start, end } = doc.data() as Shift;
        if (start < shift.end && end > shift.start) {
          isDublicate = true;
          addSnackbar(t.save_shift_error_message[lang], 'error');
        }
      });

      if (!isDublicate) {
        await setDoc<Shift>(`cafes/${cafeSlug}/shifts/${uniqid()}`, {
          start,
          end,
          workerId: shift.workerId,
          workerName: shift.workerName,
          ratePerHour: shift.ratePerHour,
          percentageOfSales: shift.percentageOfSales,
          comment: shift.comment,
          paid: false,
          bonus: shift.bonus,
        });
      }
    },
    [
      addSnackbar,
      cafeSlug,
      lang,
      shift.bonus,
      shift.comment,
      shift.end,
      shift.percentageOfSales,
      shift.ratePerHour,
      shift.start,
      shift.workerId,
      shift.workerName,
    ]
  );

  const handleSubbmit = useCallback(
    async (value: FormType) => {
      if (value.mode === 'date' && date) {
        await saveShift(date);
      }

      if (value.mode === 'perWeek') {
        const datesUntillEndOfMonth: Date[] = [];

        for (let d = addDays(shift.start, 1); getMonth(d) === getMonth(shift.start); d = addDays(d, 1)) {
          const key = `day${d.getDay() || 7}` as 'day1';
          if (value[key]) {
            datesUntillEndOfMonth.push(new Date(d));
          }
        }

        await datesUntillEndOfMonth.reduce<Promise<void> | undefined>(async (memo, date) => {
          await memo;
          await saveShift(date);
        }, undefined);
      }

      if (value.mode === 'perDay') {
        const datesUntillEndOfMonth: Date[] = [];

        for (
          let d = addDays(shift.start, Number(value.perDays));
          getMonth(d) === getMonth(shift.start);
          d = addDays(d, Number(value.perDays))
        ) {
          datesUntillEndOfMonth.push(new Date(d));
        }

        await datesUntillEndOfMonth.reduce<Promise<void> | undefined>(async (memo, date) => {
          await memo;
          await saveShift(date);
        }, undefined);
      }

      onClose();
    },
    [date, saveShift, shift, onClose]
  );

  return (
    <EditModal<FormType>
      title={t.duplicate[lang]}
      open={open}
      onClose={onClose}
      submitButton={shift ? t.save_changes[lang] : t.add[lang]}
      onSubmit={handleSubbmit}
      form={form}
      size="xs"
    >
      <FormControl>
        <RadioGroup value={mode} onChange={handleChangeMode}>
          <FormControlLabel value="date" control={<Radio />} label={t.on_certain_day[lang]} />
          <Box sx={{ mt: 1, ml: 4, mb: 3, width: 180 }}>
            <DatePicker
              label={t.date[lang]}
              value={date}
              onChange={(newValue) => setDate(newValue)}
              renderInput={(params) => <TextField {...params} />}
              disabled={mode !== 'date'}
            />
          </Box>

          <FormControlLabel value="perWeek" control={<Radio />} label={t.on_certain_days_of_the_week[lang]} />
          <Box sx={{ ml: 4, mb: 3, display: 'flex' }}>
            {[1, 2, 3, 4, 5, 6, 7].map((index) => (
              <FormSwitch
                key={index}
                name={`day${index || 7}`}
                checkbox
                label={dayOfTheWeek[index - 1] || ''}
                disabled={mode !== 'perWeek'}
                labelPlacement="bottom"
              />
            ))}
          </Box>

          <FormControlLabel value="perDay" control={<Radio />} label={t.every_n_days[lang]} />
          <Box sx={{ mt: 1, ml: 4, mb: 3, width: 180 }}>
            <FormTextField
              name="perDays"
              type="number"
              label={t.after_every[lang]}
              rules={{ validate: (v) => Number(v) >= 1 && Number(v) <= 20 }}
              fullWidth
              disabled={mode !== 'perDay'}
              InputProps={{
                endAdornment: <InputAdornment position="start">{t.days[lang]}</InputAdornment>,
              }}
            />
          </Box>
        </RadioGroup>
      </FormControl>
    </EditModal>
  );
};

export default DuplicateModal;
